import { useState, useEffect } from 'react';
import { DEFAULT_FORM_DATA } from '../utils/reportConstants';
import treatmentReportsService from '../../../services/treatmentReportsService';

export const useReportForm = (report, userProfile, initialContext = {}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [technicians, setTechnicians] = useState([]);

  // Get franchisee ID helper
  const getFranchiseeId = () => {
    if (userProfile?.role === 'admin') {
      return userProfile.franchiseeId || 'all';
    } else if (userProfile?.role === 'franchisee') {
      return userProfile.id;
    } else if (userProfile?.role === 'technician' && (userProfile?.franchiseeId || userProfile?.assignedFranchiseeId)) {
      return userProfile.franchiseeId || userProfile.assignedFranchiseeId;
    }
    return null;
  };

  // Initialize form data
  useEffect(() => {
    const initializeForm = async () => {
      const franchiseeId = getFranchiseeId();
      if (!franchiseeId) return;

      if (report) {
        // Editing existing report — fill treatment contact/address from job if missing
        const job = initialContext?.job;
        const contactFallback = {};
        if (job) {
          // Always pull job_category_id from the job (not stored on report)
          if (job.job_category_id) contactFallback.job_category_id = job.job_category_id;
          const contactFields = ['treatment_contact_name', 'treatment_contact_company', 'treatment_contact_email', 'treatment_contact_phone'];
          const addressFields = ['treatment_address_line1', 'treatment_address_line2', 'treatment_address_city', 'treatment_address_state', 'treatment_address_postcode', 'treatment_address_country'];
          [...contactFields, ...addressFields].forEach(f => {
            if (!report[f] && job[f]) contactFallback[f] = job[f];
          });
        }
        setFormData({
          ...report,
          ...contactFallback,
          service_date: report.service_date ? report.service_date.split('T')[0] : '',
          follow_up_date: report.follow_up_date || '',
          signature_date: report.signature_date ? report.signature_date.split('T')[0] : '',
          treatment_areas: report.treatment_areas || [],
          photos: report.photos || [],
          line_items: report.line_items || []
        });
      } else {
        // Creating new report — number is auto-generated by backend on save
        // Build initial data from context if provided
        const contextData = {};
        if (initialContext.customer) contextData.customer_id = initialContext.customer.id;
        if (initialContext.job) {
          const job = initialContext.job;
          contextData.job_id = job.id;
          if (job.job_category_id) contextData.job_category_id = job.job_category_id;
          if (job.category_path) contextData.category_path = job.category_path;
          if (job.treatment_contact_name) contextData.treatment_contact_name = job.treatment_contact_name;
          if (job.treatment_contact_company) contextData.treatment_contact_company = job.treatment_contact_company;
          if (job.treatment_contact_email) contextData.treatment_contact_email = job.treatment_contact_email;
          if (job.treatment_contact_phone) contextData.treatment_contact_phone = job.treatment_contact_phone;
          if (job.treatment_address_line1) contextData.treatment_address_line1 = job.treatment_address_line1;
          if (job.treatment_address_line2) contextData.treatment_address_line2 = job.treatment_address_line2;
          if (job.treatment_address_city) contextData.treatment_address_city = job.treatment_address_city;
          if (job.treatment_address_state) contextData.treatment_address_state = job.treatment_address_state;
          if (job.treatment_address_postcode) contextData.treatment_address_postcode = job.treatment_address_postcode;
          if (job.treatment_address_country) contextData.treatment_address_country = job.treatment_address_country;
        }
        if (initialContext.appointment) {
          contextData.appointment_id = initialContext.appointment.id;
          if (initialContext.appointment.scheduled_date) {
            contextData.service_date = new Date(initialContext.appointment.scheduled_date).toISOString().split('T')[0];
          }
        }

        // Determine technician_id
        let technicianId = '';
        if (userProfile.role === 'technician') {
          technicianId = userProfile.id;
        } else if (initialContext.job) {
          const job = initialContext.job;
          const jobFranchiseeId = job.franchisee_id || franchiseeId;
          if (Number(job.branch_id) === 1 || !job.branch_id) {
            technicianId = job.franchisee_id || '';
          } else {
            try {
              const techResponse = await treatmentReportsService.getTechnicians(jobFranchiseeId);
              const techs = techResponse.technicians || [];
              const tech = techs.find(t => Number(t.branch_id) === Number(job.branch_id));
              if (tech) {
                technicianId = tech.id;
              } else if (techs.length > 0) {
                const franchiseeUser = techs.find(t => t.role === 'franchisee');
                if (franchiseeUser) technicianId = franchiseeUser.id;
              }
            } catch (err) {
              console.error('Error resolving technician from job:', err);
            }
          }
        }

        setFormData(prev => ({
          ...prev,
          service_date: contextData.service_date || new Date().toISOString().split('T')[0],
          technician_id: technicianId,
          ...contextData
        }));
      }

      // Load technicians
      try {
        const response = await treatmentReportsService.getTechnicians(franchiseeId);
        if (response.technicians) {
          setTechnicians(response.technicians);
        }
      } catch (error) {
        console.error('Error loading technicians:', error);
      }
    };

    initializeForm();
  }, [report, userProfile]);

  // Update form field
  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Update multiple form fields
  const updateFormFields = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  return {
    formData,
    setFormData,
    technicians,
    updateFormField,
    updateFormFields,
    getFranchiseeId
  };
};